import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import React, {useState} from 'react';
import './HorizontalStatusTracker.sass';
import EnrollmentFileProcessingStep from './EnrollmentFileProcessingStep';
import SuccessfulRecords from './SuccessfulRecords';
import UnsuccessfulRecords from './UnsuccessfulRecords';
import InProgressRecords from './InProgressRecords';
import CancelledRecords from './CancelledRecords';
import {Button, MobileStepper, useMediaQuery} from '@material-ui/core';
import {theme} from 'Core/components/WithTheme';
import {ExternalEnrollmentFileUploadSteps} from 'Shared/constants/ExternalEnrollmentFileUploadSteps';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@material-ui/icons';

type StepEnum = {
  id: number;
  value: string;
};
type GenelcoSpreadsheetHorizontalStatusTrackerProps = {
  enrollmentFile: EnrollmentApplication;
  steps: StepEnum[];
  currentStep?: number;
};

const GenelcoSpreadsheetHorizontalStatusTracker = ({
  enrollmentFile,
  steps,
  currentStep,
}: GenelcoSpreadsheetHorizontalStatusTrackerProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(currentStep!);
  const hasProcessingCompleted = enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.COMPLETED;
  const hasGroupWebAuditCompleted = enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE;
  const recievedStepStatus = enrollmentFile.validRecordCount! === 0 ? 'error' : 'completed';
  const isAuditInitiated = enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.READY_FOR_AUDIT;
  const isProcessingInProgress =
    enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE ||
    enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.AUDIT_COMPLETE;
  const isCanceledProcessing = enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.CANCELED;

  const getFileUploadStepStatus = () => {
    if (enrollmentFile.validAuditCount! === 0 && enrollmentFile!.invalidAuditCount === 0) {
      return 'error';
    } else if (enrollmentFile.validRecordCount! > 0 && enrollmentFile.invalidRecordCount! > 0) {
      return 'warning';
    } else {
      return 'completed';
    }
  };

  const getInProgressStepStatus = () => {
    if (isAuditInitiated) {
      return 'current';
    } else if (isCanceledProcessing) {
      return 'canceled';
    } else if (isProcessingInProgress && enrollmentFile.validRecordCount! === 0) {
      return 'inactive';
    } else if (isProcessingInProgress && enrollmentFile.validAuditCount! > 0 && enrollmentFile.invalidAuditCount! > 0) {
      return 'warning';
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validAuditCount! > 0 &&
      enrollmentFile.invalidAuditCount! > 0
    ) {
      return 'warning';
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!
    ) {
      return 'error';
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!
    ) {
      return 'completed';
    } else if (
      (isProcessingInProgress && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!) ||
      hasProcessingCompleted ||
      (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!)
    ) {
      return 'completed';
    } else if (
      (isProcessingInProgress && enrollmentFile.invalidRecordCount! === enrollmentFile.invalidAuditCount!) ||
      hasProcessingCompleted ||
      (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!)
    ) {
      return 'error';
    } else {
      return 'error';
    }
  };

  const getFileUploadIconStatus = () => {
    if (enrollmentFile.validAuditCount! === 0 && enrollmentFile!.invalidAuditCount === 0) {
      return 'fas fa-times-circle status__unsuccessful';
    } else if (enrollmentFile.validRecordCount! > 0 && enrollmentFile.invalidRecordCount! > 0) {
      return 'fas fa-exclamation-triangle status__warning';
    } else {
      return 'fas fa-check-circle status__successful';
    }
  };
  const getInProgressIconStatus = () => {
    if (isAuditInitiated) {
      return 'far fa-circle status__processing';
    } else if (isCanceledProcessing) {
      return `fas fa-times-circle status__canceled`;
    } else if (isProcessingInProgress && enrollmentFile.validRecordCount! === 0) {
      return 'far fa-circle status__inactive';
    } else if (isProcessingInProgress && enrollmentFile.validAuditCount! > 0 && enrollmentFile.invalidAuditCount! > 0) {
      return `fas fa-exclamation-triangle status__warning`;
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validAuditCount! > 0 &&
      enrollmentFile.invalidAuditCount! > 0
    ) {
      return `fas fa-exclamation-triangle status__warning`;
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!
    ) {
      return 'fas fa-times-circle status__unsuccessful';
    } else if (
      (hasProcessingCompleted || hasGroupWebAuditCompleted) &&
      enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!
    ) {
      return 'fas fa-check-circle status__successful';
    } else if (
      (isProcessingInProgress && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!) ||
      hasProcessingCompleted ||
      (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!)
    ) {
      return 'fas fa-check-circle status__successful';
    } else if (
      (isProcessingInProgress && enrollmentFile.invalidRecordCount! === enrollmentFile.invalidAuditCount!) ||
      (hasProcessingCompleted &&
        hasGroupWebAuditCompleted &&
        enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!)
    ) {
      return `fas fa-times-circle status__unsuccessful`;
    } else {
      return 'fas fa-times-circle status__unsuccessful';
    }
  };

  const inProgressStepStatus = getInProgressStepStatus();
  const inProgressIconStatus = getInProgressIconStatus();
  const fileUploadStepStatus = getFileUploadStepStatus();
  const fileUploadIconStatus = getFileUploadIconStatus();

  const getProcessCompleteStepStatus = () => {
    if (hasProcessingCompleted && enrollmentFile.validAuditCount! > 0) {
      return 'current';
    } else if (hasProcessingCompleted && enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!) {
      return 'inactive';
    } else if (
      hasGroupWebAuditCompleted &&
      enrollmentFile.validAuditCount! > 0 &&
      enrollmentFile.invalidAuditCount! > 0
    ) {
      return 'warning';
    } else if (
      hasGroupWebAuditCompleted &&
      enrollmentFile.validAuditCount! === enrollmentFile.groupWebComparisonErrorCount!
    ) {
      return 'error';
    } else if (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!) {
      return 'error';
    } else if (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!) {
      return 'completed';
    } else {
      return 'inactive';
    }
  };

  const getProcessCompleteIconStatus = () => {
    if (hasProcessingCompleted && enrollmentFile.validAuditCount! > 0) {
      return 'far fa-circle status__processing';
    } else if (hasProcessingCompleted && enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!) {
      return 'far fa-circle status__inactive';
    } else if (
      hasGroupWebAuditCompleted &&
      enrollmentFile.validAuditCount! > 0 &&
      enrollmentFile.invalidAuditCount! > 0
    ) {
      return 'fas fa-exclamation-triangle status__warning';
    } else if (
      hasGroupWebAuditCompleted &&
      enrollmentFile.validAuditCount! === enrollmentFile.groupWebComparisonErrorCount!
    ) {
      return 'fas fa-times-circle status__unsuccessful';
    } else if (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.invalidAuditCount!) {
      return 'fas fa-times-circle status__unsuccessful';
    } else if (hasGroupWebAuditCompleted && enrollmentFile.validRecordCount! === enrollmentFile.validAuditCount!) {
      return 'fas fa-check-circle status__successful';
    } else {
      return 'far fa-circle status__inactive';
    }
  };

  const processCompleteStepStatus = getProcessCompleteStepStatus();
  const processCompleteIconStatus = getProcessCompleteIconStatus();

  const fileUploadStepStatusDetail = (
    <EnrollmentFileProcessingStep
      stepName="File Uploaded"
      titleClassName={fileUploadStepStatus}
      statusIcon={<i className={fileUploadIconStatus} />}
    >
      {enrollmentFile.validRecordCount! > 0 && <SuccessfulRecords count={enrollmentFile.validRecordCount!} />}
      {enrollmentFile.invalidRecordCount! > 0 && <UnsuccessfulRecords count={enrollmentFile.invalidRecordCount!} />}
    </EnrollmentFileProcessingStep>
  );

  const receivedStepStatusDetail = (
    <EnrollmentFileProcessingStep
      stepName="Received"
      titleClassName={recievedStepStatus}
      statusIcon={
        <i
          className={`fas ${
            enrollmentFile.validRecordCount! === 0
              ? 'fa-times-circle status__unsuccessful'
              : 'fa-check-circle status__successful'
          }`}
        />
      }
    >
      {enrollmentFile.validRecordCount! > 0 && <SuccessfulRecords count={enrollmentFile.validRecordCount!} />}
    </EnrollmentFileProcessingStep>
  );

  const processingStepStatusDetail = (
    <EnrollmentFileProcessingStep
      stepName="Processed"
      titleClassName={enrollmentFile.validRecordCount! > 0 ? inProgressStepStatus : 'inactive'}
      statusIcon={
        <i className={enrollmentFile.validRecordCount! > 0 ? inProgressIconStatus : 'far fa-circle status__inactive'} />
      }
    >
      {!isCanceledProcessing && enrollmentFile.validAuditCount! > 0 && isAuditInitiated && (
        <InProgressRecords count={enrollmentFile.validAuditCount!} />
      )}
      {!isCanceledProcessing && enrollmentFile.validAuditCount! > 0 && !isAuditInitiated && (
        <SuccessfulRecords count={enrollmentFile.validAuditCount!} />
      )}
      {!isCanceledProcessing && enrollmentFile.invalidAuditCount! > 0 && !isAuditInitiated && (
        <UnsuccessfulRecords count={enrollmentFile.invalidAuditCount!} />
      )}
      {isCanceledProcessing && <CancelledRecords count={enrollmentFile.invalidAuditCount!} />}
    </EnrollmentFileProcessingStep>
  );

  const processingCompleteStepStatusDetail = (
    <EnrollmentFileProcessingStep
      stepName="Confirmed"
      titleClassName={processCompleteStepStatus}
      statusIcon={
        <i
          className={
            (hasProcessingCompleted || hasGroupWebAuditCompleted) && enrollmentFile.validRecordCount! > 0
              ? processCompleteIconStatus
              : 'far fa-circle status__inactive'
          }
        />
      }
    >
      {enrollmentFile.validAuditCount! > 0 && enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.COMPLETED && (
        <InProgressRecords count={enrollmentFile.validAuditCount!} />
      )}
      {enrollmentFile.validAuditCount! > 0 &&
        enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE && (
          <SuccessfulRecords count={enrollmentFile.validAuditCount! - enrollmentFile.groupWebComparisonErrorCount!} />
        )}
      {enrollmentFile.validAuditCount! > 0 &&
        enrollmentFile.groupWebComparisonErrorCount! > 0 &&
        enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE && (
          <UnsuccessfulRecords count={enrollmentFile.groupWebComparisonErrorCount!} />
        )}
    </EnrollmentFileProcessingStep>
  );

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };
  const getCurrentEnrollmentFileProcessingStep = (activeStep: number) => {
    switch (activeStep) {
      case ExternalEnrollmentFileUploadSteps.FileUpload:
        return fileUploadStepStatusDetail;
      case ExternalEnrollmentFileUploadSteps.Received:
        return receivedStepStatusDetail;
      case ExternalEnrollmentFileUploadSteps.Processing:
        return processingStepStatusDetail;
      case ExternalEnrollmentFileUploadSteps.ProcessingComplete:
        return processingCompleteStepStatusDetail;
    }
  };

  const maxSteps = steps.length;
  return isMobile ? (
    <div className="mobile-status-tracker">
      {getCurrentEnrollmentFileProcessingStep(activeStep)}
      <div>
        <MobileStepper
          steps={maxSteps}
          position="static"
          variant="text"
          activeStep={activeStep}
          nextButton={
            <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
              {activeStep === maxSteps - 1 ? steps[activeStep].value : steps[activeStep + 1].value}
              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
              {activeStep === 0 ? steps[activeStep].value : steps[activeStep - 1].value}
            </Button>
          }
        />
      </div>
    </div>
  ) : (
    <div className="horizontal-status-tracker" id="horizontal-status-tracker">
      {fileUploadStepStatusDetail}
      <span className="horizontal-status-tracker__divider completed" />
      {receivedStepStatusDetail}
      <span
        className={`horizontal-status-tracker__divider ${enrollmentFile.validRecordCount! > 0 ? 'completed' : ''}`}
      />

      {processingStepStatusDetail}
      <span
        className={`horizontal-status-tracker__divider ${
          hasProcessingCompleted && enrollmentFile.validAuditCount! > 0 ? 'completed' : ''
        }`}
      />
      {processingCompleteStepStatusDetail}
    </div>
  );
};

export default GenelcoSpreadsheetHorizontalStatusTracker;
